<template>
  <div class="calm pp">
    <!-- ═══════════════ HEADER ═══════════════ -->
    <header class="calm__header">
      <div class="calm__header__row">
        <div>
          <div class="c-eyebrow">PromptPay</div>
          <h1 class="calm__header__title">บัญชี PromptPay ของสาขา</h1>
          <div class="calm__header__sub">
            <span>ใช้สร้าง QR รับชำระในหน้า Quick Payment</span>
            <span class="calm__header__sep">·</span>
            <span><strong class="c-mono">{{ accounts.length }}</strong> บัญชี</span>
          </div>
        </div>
        <button class="c-btn c-btn--primary" @click="openCreate">
          <i class="fas fa-plus mr-1"></i> เพิ่มบัญชี
        </button>
      </div>
    </header>

    <div class="calm__body">
      <div class="calm__card calm__card--flush">
        <div class="calm__table-wrap">
          <table class="calm__table">
            <thead>
              <tr>
                <th>ประเภท</th>
                <th>เลขรับเงิน</th>
                <th>ชื่อแสดง</th>
                <th>สถานะ</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="accounts.length === 0">
                <td colspan="5" class="calm__empty">ยังไม่มีบัญชี — กด “เพิ่มบัญชี” เพื่อเริ่ม</td>
              </tr>
              <tr v-for="a in accounts" :key="a.ID">
                <td>{{ receiverTypeName(a.ReceiverType) }}</td>
                <td class="c-mono">{{ a.ReceiverID }}</td>
                <td>{{ a.DisplayName || "—" }}</td>
                <td>
                  <span class="calm__pill" :class="a.IsActive === 1 ? 'pp-pill--on' : 'pp-pill--off'">
                    {{ a.IsActive === 1 ? "ใช้งาน" : "ปิด" }}
                  </span>
                </td>
                <td class="ta-r">
                  <button class="c-btn" @click="openEdit(a)"><i class="fas fa-pen"></i></button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <!-- ═══════════════ Modal ═══════════════ -->
    <div v-if="showForm" class="pp-modal" @click.self="showForm = false">
      <div class="calm pp-modal__box">
        <h3 class="calm__card__title">{{ form.ID ? "แก้ไขบัญชี" : "เพิ่มบัญชี PromptPay" }}</h3>
        <div class="calm__filter-grid pp-form">
          <div class="fld">
            <label>ประเภทผู้รับ</label>
            <el-select v-model="form.ReceiverType" placeholder="ประเภท">
              <el-option v-for="t in receiverTypes" :key="t.ID" :label="t.Name" :value="t.ID"></el-option>
            </el-select>
          </div>
          <div class="fld">
            <label>เลขรับเงิน<span class="pp-req">*</span></label>
            <input v-model="form.ReceiverID" class="pp-input" type="text" :placeholder="receiverHint" />
            <div class="pp-hint">{{ receiverHint }}</div>
          </div>
          <div class="fld">
            <label>ชื่อแสดง (เช่น ชื่อสาขา/บริษัท)</label>
            <input v-model="form.DisplayName" class="pp-input" type="text" placeholder="ไม่บังคับ" />
          </div>
          <div class="fld" v-if="form.ID">
            <label>สถานะ</label>
            <el-select v-model="form.IsActive" placeholder="สถานะ">
              <el-option :label="'ใช้งาน'" :value="1"></el-option>
              <el-option :label="'ปิด'" :value="0"></el-option>
            </el-select>
          </div>
        </div>
        <div class="pp-modal__actions">
          <button class="c-btn" @click="showForm = false">ยกเลิก</button>
          <button class="c-btn c-btn--primary" :disabled="saving || !form.ReceiverID" @click="save">
            {{ saving ? "กำลังบันทึก..." : "บันทึก" }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { Select, Option } from "element-ui";
import { mapGetters, mapActions } from "vuex";
import swal from "sweetalert2";

export default {
  components: {
    [Select.name]: Select,
    [Option.name]: Option,
  },
  data() {
    return {
      receiverTypes: [
        { ID: 1, Name: "เบอร์โทรศัพท์" },
        { ID: 2, Name: "เลขประจำตัวผู้เสียภาษี / บัตรประชาชน" },
        { ID: 3, Name: "e-Wallet ID" },
      ],
      showForm: false,
      saving: false,
      form: { ID: 0, ReceiverType: 1, ReceiverID: "", DisplayName: "", IsActive: 1 },
    };
  },
  computed: {
    ...mapGetters({ accounts: "QuickPay/getPromptPay" }),
    receiverHint() {
      switch (this.form.ReceiverType) {
        case 1: return "เบอร์โทร 10 หลัก เช่น 0812345678";
        case 2: return "เลขผู้เสียภาษี/บัตรประชาชน 13 หลัก";
        case 3: return "e-Wallet ID 15 หลัก";
        default: return "";
      }
    },
  },
  mounted() {
    this.loadPromptPay();
  },
  methods: {
    ...mapActions({
      loadPromptPay: "QuickPay/loadPromptPay",
      savePromptPay: "QuickPay/savePromptPay",
      updatePromptPay: "QuickPay/updatePromptPay",
    }),
    receiverTypeName(id) {
      const t = this.receiverTypes.find((x) => x.ID === id);
      return t ? t.Name : "—";
    },
    openCreate() {
      this.form = { ID: 0, ReceiverType: 1, ReceiverID: "", DisplayName: "", IsActive: 1 };
      this.showForm = true;
    },
    openEdit(a) {
      this.form = {
        ID: a.ID,
        ReceiverType: a.ReceiverType,
        ReceiverID: a.ReceiverID,
        DisplayName: a.DisplayName,
        IsActive: a.IsActive,
      };
      this.showForm = true;
    },
    async save() {
      this.saving = true;
      try {
        const data = {
          ReceiverType: this.form.ReceiverType,
          ReceiverID: this.form.ReceiverID,
          DisplayName: this.form.DisplayName,
          IsActive: this.form.IsActive,
        };
        if (this.form.ID) {
          await this.updatePromptPay({ id: this.form.ID, data });
        } else {
          await this.savePromptPay(data);
        }
        this.showForm = false;
        await this.loadPromptPay();
        swal.fire({ title: "บันทึกสำเร็จ", icon: "success", timer: 1200, showConfirmButton: false });
      } catch (err) {
        const msg = err && err.response && err.response.data ? err.response.data.Cause || err.response.data.Msg : "บันทึกไม่สำเร็จ";
        swal.fire({ title: "ผิดพลาด", text: msg, icon: "error" });
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style scoped>
.pp .ta-r { text-align: right; }
.pp-input {
  width: 100%;
  height: 40px;
  padding: 0 12px;
  border: 1px solid var(--c-border-strong, #c7d1de);
  border-radius: 8px;
  background: var(--c-surface, #fff);
  color: var(--c-text, #2c3e50);
  font-size: 15px;
}
.pp-input:focus { outline: none; border-color: var(--c-blue, #3b6ea8); }
.pp-hint { font-size: 12px; color: var(--c-text-mute, #8a96a8); margin-top: 4px; }
.pp-req { color: #c0392b; margin-left: 2px; }
.pp-pill--on { background: rgba(56, 134, 107, 0.12); color: var(--c-success, #38866b); }
.pp-pill--off { background: rgba(138, 150, 168, 0.15); color: var(--c-text-mute, #8a96a8); }

.pp-modal {
  position: fixed; inset: 0; background: rgba(20, 30, 45, 0.45);
  display: flex; align-items: center; justify-content: center; z-index: 2000;
}
.pp-modal__box {
  background: var(--c-surface, #fff); border-radius: 14px; padding: 24px 28px;
  width: 460px; max-width: 92vw; box-shadow: 0 12px 40px rgba(0,0,0,.2);
}
.pp-form { margin-top: 14px; grid-template-columns: 1fr; }
.pp-modal__actions { display: flex; gap: 10px; margin-top: 18px; justify-content: flex-end; }
</style>
